import React, {FC, useEffect, useMemo, useState} from "react";
import {Button, Result, Spin} from "antd";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import "../Styles/PublicRegistrationPage.css";
import {PublicShell} from "../Components/ui/PublicShell";
import {readDownloadErrorMessage} from "../downloadFile";
import {publicEmailConfirmationLabelKeys} from "../i18n/publicPages";
import {usePublicPageLabels} from "../i18n/publicPageLabels";

type ConfirmationLanguage = "hr" | "en";
type ConfirmationState =
    | {kind: "loading"}
    | {kind: "success"; alreadyConfirmed: boolean}
    | {kind: "error"; reason: "expired" | "invalid" | "generic"};

type ConfirmationResponse = {
    registrationId: number;
    verifiedAtUtc: string;
    participationOfferNumber: string;
    accommodationOfferNumber: string;
    alreadyConfirmed: boolean;
};

const classifyConfirmationError = (message: string): "expired" | "invalid" | "generic" => {
    if (/expired/i.test(message)) {
        return "expired";
    }

    if (/invalid|required|unavailable|not found/i.test(message)) {
        return "invalid";
    }

    return "generic";
};

const PublicRegistrationEmailConfirmationPage: FC = () => {
    const {eventIdentifier, registrationId} = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("token")?.trim() ?? "";
    const language: ConfirmationLanguage = searchParams.get("lang")?.toLowerCase().startsWith("en")
        ? "en"
        : "hr";
    const labels = usePublicPageLabels("public-email-confirmation", publicEmailConfirmationLabelKeys, language);
    const [state, setState] = useState<ConfirmationState>({kind: "loading"});

    useEffect(() => {
        const parsedRegistrationId = Number(registrationId);
        if (!eventIdentifier || !Number.isInteger(parsedRegistrationId) || parsedRegistrationId <= 0 || !token) {
            setState({kind: "error", reason: "invalid"});
            return;
        }

        const abortController = new AbortController();
        setState({kind: "loading"});

        void fetch(
            `/api/public/events/${encodeURIComponent(eventIdentifier)}/registrations/${parsedRegistrationId}/confirm-email?token=${encodeURIComponent(token)}`,
            {signal: abortController.signal}
        ).then(async response => {
            if (!response.ok) {
                throw new Error(await readDownloadErrorMessage(response, ""));
            }

            return await response.json() as ConfirmationResponse;
        }).then(response => {
            setState({kind: "success", alreadyConfirmed: response.alreadyConfirmed === true});
        }).catch(error => {
            if (error instanceof DOMException && error.name === "AbortError") {
                return;
            }

            const message = error instanceof Error ? error.message : "";
            setState({kind: "error", reason: classifyConfirmationError(message)});
        });

        return () => abortController.abort();
    }, [eventIdentifier, registrationId, token]);

    const presentation = useMemo(() => {
        if (state.kind === "loading") {
            return {
                status: "info" as const,
                title: labels.loadingTitle,
                subTitle: labels.loadingHelp,
                icon: <Spin/>
            };
        }

        if (state.kind === "success") {
            return {
                status: "success" as const,
                title: state.alreadyConfirmed ? labels.alreadyTitle : labels.successTitle,
                subTitle: state.alreadyConfirmed ? labels.alreadyHelp : labels.successHelp,
                icon: undefined
            };
        }

        const errorCopy = {
            expired: [labels.expiredTitle, labels.expiredHelp],
            invalid: [labels.invalidTitle, labels.invalidHelp],
            generic: [labels.genericTitle, labels.genericHelp]
        }[state.reason];

        return {
            status: "error" as const,
            title: errorCopy[0],
            subTitle: errorCopy[1],
            icon: undefined
        };
    }, [labels, state]);

    const returnToRegistration = () => {
        if (!eventIdentifier) {
            return;
        }

        navigate(`/public/events/${encodeURIComponent(eventIdentifier)}/register?lang=${language}`);
    };

    // A confirmed link opens the same registration details page as the QR invitation.
    const openRegistrationDetails = () => {
        if (!eventIdentifier) {
            return;
        }

        const query = new URLSearchParams({registrationId: String(registrationId), confirmationToken: token, lang: language});
        navigate(`/public/events/${encodeURIComponent(eventIdentifier)}/invitation?${query.toString()}`);
    };

    return (
        <PublicShell live>
                <Result
                    status={presentation.status}
                    icon={presentation.icon}
                    title={presentation.title}
                    subTitle={presentation.subTitle}
                    extra={!eventIdentifier || state.kind === "loading" ? undefined : state.kind === "success" ? (
                        <Button type="primary" onClick={openRegistrationDetails}>
                            {labels.viewRegistrationDetails}
                        </Button>
                    ) : (
                        <Button type="primary" onClick={returnToRegistration}>
                            {labels.returnToRegistration}
                        </Button>
                    )}
                />
        </PublicShell>
    );
};

export default PublicRegistrationEmailConfirmationPage;
